import React, { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { PageHeader, AdminDataGrid } from '../../../components/admin';
import HydrantFormDialog from '../../../components/hydranty/HydrantFormDialog';
import HydrantImportDialog from '../../../components/hydranty/HydrantImportDialog';
import {
  useHydrantyQuery, useCreateHydrantMutation, useUpdateHydrantMutation, useDeleteHydrantMutation,
} from '../../../api/JsdhNotifikaceService/service/HydrantBodMutations';
import { HydrantBodService } from '../../../api/JsdhNotifikaceService/service/HydrantBodService';
import { HydrantBod } from '../../../api/JsdhNotifikaceService/generated/models/HydrantBod';

const HydrantSeznamPage: React.FC = () => {
  const { data: body, isLoading } = useHydrantyQuery();
  const create = useCreateHydrantMutation();
  const update = useUpdateHydrantMutation();
  const remove = useDeleteHydrantMutation();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [edited, setEdited] = useState<HydrantBod | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const columns: GridColDef[] = useMemo(() => [
    { field: 'oznaceni', headerName: 'Označení', flex: 1 },
    { field: 'typ', headerName: 'Typ', flex: 1 },
    { field: 'dn', headerName: 'DN', width: 80 },
    { field: 'ulice', headerName: 'Ulice', flex: 1.5 },
    { field: 'aktivni', headerName: 'Aktivní', width: 90,
      valueFormatter: (v) => (v ? 'Ano' : 'Ne') },
    {
      field: 'akce', headerName: 'Akce', width: 140, sortable: false,
      renderCell: (p) => (
        <>
          <IconButton size="small" onClick={() => navigate(`/admin/hydranty-evidence/${p.row.id}`)}>
            <span style={{ fontSize: 12 }}>Detail</span>
          </IconButton>
          <IconButton size="small" onClick={() => { setEdited(p.row); setFormOpen(true); }}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteId(p.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </>
      ),
    },
  ], [navigate, update, remove]);

  return (
    <Box>
      <PageHeader title="Seznam hydrantů" action={
        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEdited(null); setFormOpen(true); }}>Přidat</Button>
          <Button startIcon={<UploadIcon />} onClick={() => setImportOpen(true)}>Import</Button>
          <Button startIcon={<DownloadIcon />} onClick={() => HydrantBodService.stahniCsv()}>CSV</Button>
          <Button startIcon={<DownloadIcon />} onClick={() => HydrantBodService.stahniQrStitky((body ?? []).map((h) => h.id))}>QR štítky</Button>
        </Stack>
      } />
      <AdminDataGrid rows={body ?? []} columns={columns} loading={isLoading} getRowId={(r) => r.id} />

      <HydrantFormDialog open={formOpen} hydrant={edited} onClose={() => setFormOpen(false)}
        onSubmit={(data) => {
          if (edited) update.mutate({ id: edited.id, data });
          else create.mutate(data);
          setFormOpen(false);
        }} />
      <HydrantImportDialog open={importOpen} onClose={() => setImportOpen(false)} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Smazat hydrant?</DialogTitle>
        <DialogContent>
          <Typography>Opravdu chcete smazat tento hydrant? Akce je nevratná.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Zrušit</Button>
          <Button variant="contained" color="error" disabled={remove.isPending}
            onClick={() => { if (deleteId !== null) { remove.mutate(deleteId); setDeleteId(null); } }}>
            Smazat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HydrantSeznamPage;
